import * as React from 'react';
import {
    getTriggerId,
    isCompleteAccountModalOpen,
    isFollowSearchModalOpen,
    isLoginModalOpen,
    isTracking,
    openCompleteAccountModalFromChecker,
    openConfirmPasswordModalFromChecker,
    openFollowSearchModalFromChecker,
    openLoginModalFromChecker,
    openSetPasswordModalFromChecker,
    openSignUpModalFromChecker,
    stopTracker,
} from '@/redux/modules/shouldLogin';
import {
    isConfirmPasswordTriggered,
    isProfileComplete,
    isUserAtLeastPartiallyRegistered,
    isUserLoggedIn,
    isUserPartiallyRegistered,
} from '@/redux/modules/user';
import { ModalContext, setModalContext } from '@/redux/modules/modal';
import { useDispatch, useSelector } from 'react-redux';
import isEqual from 'react-fast-compare';
import { SearchParams as SearchParamsType } from '@/types/SaveSearch';
import usePrevious from '@/hooks/usePrevious';
import { v4 as uuidv4 } from 'uuid';

type Props = {
    allSearchParams?: SearchParamsType;
    children: React.ReactNode;
    forceConfirmPassword?: boolean;
    forceSignUp?: boolean;
    modalContext?: ModalContext;
    requireCompleteProfile?: boolean;
    requireLogin?: boolean;
    requireUserAtLeastPartiallyRegistered?: boolean;
    runFunction: Function;
    runFunctionArgument?: any;
};

const ShouldLogInContainer = ({
    allSearchParams,
    children,
    forceConfirmPassword,
    forceSignUp = true,
    modalContext,
    requireCompleteProfile = false,
    requireLogin = true,
    requireUserAtLeastPartiallyRegistered,
    runFunction,
    runFunctionArgument,
}: Props) => {
    const guid = React.useMemo(() => uuidv4(), []);
    const dispatch = useDispatch();

    const userLoggedIn = useSelector((state) => isUserLoggedIn(state));
    const isCompleteProfile = useSelector((state) => isProfileComplete(state));
    const triggerId = useSelector((state) => getTriggerId(state), isEqual);
    const tracking = useSelector((state) => isTracking(state), isEqual);
    const loginModalOpen = useSelector((state) => isLoginModalOpen(state));
    const completeAccountModalOpen = useSelector((state) => isCompleteAccountModalOpen(state));
    const followSearchOpen = useSelector((state) => isFollowSearchModalOpen(state));
    const confirmPasswordTriggered = useSelector((state) => isConfirmPasswordTriggered(state));
    const userAtLeastPartiallyRegistered = useSelector((state) => isUserAtLeastPartiallyRegistered(state));
    const userIsPartiallyRegistered = useSelector((state) => isUserPartiallyRegistered(state));

    const previousConfirmPasswordTriggered = usePrevious(confirmPasswordTriggered);

    // Determines container behavrio on change, either running passed in function, or running component logic if we're not confirming password
    React.useEffect(() => {
        if (guid === triggerId && tracking) {
            if (forceConfirmPassword) {
                if (previousConfirmPasswordTriggered && previousConfirmPasswordTriggered !== confirmPasswordTriggered) {
                    dispatch(stopTracker());
                    runFunction(runFunctionArgument);
                }
            } else {
                if (
                    !loginModalOpen &&
                    !followSearchOpen &&
                    userAtLeastPartiallyRegistered &&
                    !completeAccountModalOpen &&
                    (userLoggedIn || !requireLogin) &&
                    (isCompleteProfile || !requireCompleteProfile)
                ) {
                    dispatch(stopTracker());
                    runFunction(runFunctionArgument);
                }

                if (
                    userLoggedIn &&
                    !loginModalOpen &&
                    requireCompleteProfile &&
                    !isCompleteProfile &&
                    !completeAccountModalOpen
                ) {
                    dispatch(openCompleteAccountModalFromChecker(triggerId));
                }
            }
        }
    }, [
        completeAccountModalOpen,
        confirmPasswordTriggered,
        dispatch,
        followSearchOpen,
        forceConfirmPassword,
        guid,
        isCompleteProfile,
        loginModalOpen,
        previousConfirmPasswordTriggered,
        requireCompleteProfile,
        requireLogin,
        runFunction,
        runFunctionArgument,
        tracking,
        triggerId,
        userAtLeastPartiallyRegistered,
        userLoggedIn,
    ]);

    const determineModalFlow = (userIsPartiallyRegistered) => {
        if (userIsPartiallyRegistered) {
            dispatch(openSetPasswordModalFromChecker(guid));
        } else {
            if (forceSignUp) {
                dispatch(openSignUpModalFromChecker(guid));
            } else {
                dispatch(openLoginModalFromChecker(guid));
            }
        }
    };

    const handleClick = () => {
        if (modalContext) {
            dispatch(setModalContext(modalContext));
        }

        if (forceConfirmPassword) {
            dispatch(openConfirmPasswordModalFromChecker(guid));
        } else if (forceSignUp && !userLoggedIn && !userAtLeastPartiallyRegistered) {
            dispatch(openSignUpModalFromChecker(guid));
        } else if (forceSignUp && !userLoggedIn && requireLogin) {
            determineModalFlow(userIsPartiallyRegistered);
        } else if (!isCompleteProfile && requireCompleteProfile) {
            dispatch(openCompleteAccountModalFromChecker(guid));
        } else if (!userAtLeastPartiallyRegistered && requireUserAtLeastPartiallyRegistered) {
            determineModalFlow(userIsPartiallyRegistered);
        } else if (Boolean(allSearchParams) && !userAtLeastPartiallyRegistered) {
            dispatch(openFollowSearchModalFromChecker(guid, allSearchParams));
        } else if (runFunction) {
            runFunction(runFunctionArgument);
        }
    };

    // @ts-ignore
    return children ? React.cloneElement(React.Children.only(children), { onClick: handleClick }) : null;
};

export default React.memo<Props>(ShouldLogInContainer, isEqual);
